import { useEffect, useRef, useState } from 'react';
import { ImageOff, Upload } from 'lucide-react';
import { Button } from '@/components/ui/Button';

/**
 * Miniatura desde el storage de medios. Distingue dos casos para no mostrar una
 * miniatura rota:
 *  - sin clave  → "sin foto" (el modelo no tiene imagen).
 *  - clave rota → "falta": hay clave pero el archivo no existe (p. ej. los
 *    placeholders del seed como `catalog/inverter-12k.jpg`). Se limpia con
 *    "Quitar rotas" en la ficha del modelo.
 */
export function MediaThumb({
  mediaKey,
  alt,
  size = 'h-14 w-14',
}: {
  mediaKey: string | null;
  alt: string;
  size?: string;
}) {
  const [broken, setBroken] = useState(false);
  useEffect(() => setBroken(false), [mediaKey]);
  if (!mediaKey || broken) {
    const missing = Boolean(mediaKey); // hay clave, pero el archivo no está
    return (
      <div
        className={`flex ${size} shrink-0 flex-col items-center justify-center gap-0.5 rounded-md border border-dashed border-surface-border bg-slate-50 text-slate-400 dark:border-slate-700 dark:bg-slate-900`}
        title={missing ? `Falta el archivo: ${mediaKey}` : 'Sin foto'}
      >
        <ImageOff className="h-4 w-4" />
        <span className="text-[9px]">{missing ? 'falta' : 'sin foto'}</span>
      </div>
    );
  }
  return (
    <img
      src={`/media/${mediaKey}`}
      alt={alt}
      onError={() => setBroken(true)}
      className={`${size} shrink-0 rounded-md border border-surface-border object-cover dark:border-slate-700`}
    />
  );
}

/** Botón que abre el selector de archivo y sube una imagen. */
export function UploadImageButton({
  label,
  onFile,
  loading,
}: {
  label: string;
  onFile: (file: File) => void;
  loading?: boolean;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = ''; // permite re-subir el mismo archivo
        }}
      />
      <Button
        variant="outline"
        size="sm"
        loading={loading}
        onClick={() => inputRef.current?.click()}
      >
        <Upload className="mr-1 h-3.5 w-3.5" /> {label}
      </Button>
    </>
  );
}
